import type { TypeId } from '@unipile/db';
import type { ProviderAuthenticationIntentResponse } from '@unipile/types';
import { isCancel } from 'axios';
import { LoaderIcon } from 'lucide-react';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useRequestCheckpoint } from '../../../features/accounts/api/request-checkpoint';
import { useResendCheckpoint } from '../../../features/accounts/api/resend-checkpoint';
import { useSolveCheckpoint } from '../../../features/accounts/api/solve-checkpoint';
import { Button } from '../../shadcn/Button';
import { Item, ItemContent, ItemDescription, ItemTitle } from '../../shadcn/Item';
import { WizardStepContainer } from '../containers/WizardStepContainer';

/**
 * A checkpoint that requires a validation to be done. (In app validation, etc...)
 */
export function ValidationCheckpoint({
  onSuccess,
  onError,
  intent_id,
  title,
  description,
  matchNumber,
  tryAnotherWayButton,
  resendButton,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  intent_id: TypeId<'account'>;
  title: string;
  description: string;
  /** Number-matching approvals: the digits the user must select among the choices in their notification. */
  matchNumber?: string;
  tryAnotherWayButton?: boolean;
  resendButton?: boolean;
}) {
  const validationAbortControllerRef = useRef<AbortController | null>(null);
  const getValidationSignal = useCallback(() => validationAbortControllerRef.current?.signal, []);
  const { t } = useTranslation();

  const { mutate: mutateSolveCheckpoint } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess,
      onError: (error) => {
        // Try another way will cause the request to be cancelled, so we don't want to show an error in this case
        if (isCancel(error)) return;

        onError(error);
      },
    },
    getSignal: getValidationSignal,
  });

  const { mutate: mutateRequestCheckpoint, isPending: isRequestCheckpointPending } = useRequestCheckpoint({
    mutationConfig: {
      onSuccess,
      onError,
    },
  });

  const { mutate: mutateResendCheckpoint, isPending: isResendCheckpointPending } = useResendCheckpoint({
    mutationConfig: { onError },
  });

  /**
   * Start long polling. Whenever the validation is done, the mutation will be successful.
   * clearTimeout + fresh AbortController per effect run avoids Strict Mode aborting the only request
   * (effect cleanup runs before the deferred mutate from the previous run).
   */
  useEffect(() => {
    const controller = new AbortController();
    validationAbortControllerRef.current = controller;
    // Why use setTimeout ? Answer : https://github.com/TanStack/query/issues/5341
    const timer = setTimeout(() => {
      mutateSolveCheckpoint({ data: { code: '', intent_id } });
    }, 0);

    return () => {
      clearTimeout(timer);
      controller.abort();
      if (validationAbortControllerRef.current === controller) {
        validationAbortControllerRef.current = null;
      }
    };
  }, [intent_id, mutateSolveCheckpoint]);

  return (
    <WizardStepContainer title={title} description={description}>
      {matchNumber && (
        <Item variant="outline" className="mb-4">
          <ItemContent className="items-center">
            <ItemDescription>{t('InAppValidationCheckpoint.matchNumber')}</ItemDescription>
            <ItemTitle className="text-4xl font-semibold tabular-nums tracking-widest">{matchNumber}</ItemTitle>
          </ItemContent>
        </Item>
      )}
      <p className="text-muted-foreground rounded-md border px-4 py-3 text-center text-sm">
        {t('InAppValidationCheckpoint.automaticUpdate')}
      </p>

      <div className="mt-8 flex flex-col gap-4">
        {resendButton && (
          <Button
            onClick={() => mutateResendCheckpoint({ data: { intent_id } })}
            className="w-full cursor-pointer"
            type="button"
            variant={'outline'}
            disabled={isResendCheckpointPending || isRequestCheckpointPending}
          >
            {isResendCheckpointPending ? (
              <LoaderIcon className="animate-spin" size={20} />
            ) : (
              t('ProviderWizard.resendInAppChallenge')
            )}
          </Button>
        )}
        {tryAnotherWayButton && (
          <Button
            onClick={() => {
              validationAbortControllerRef.current?.abort();
              validationAbortControllerRef.current = null;
              mutateRequestCheckpoint({ data: { intent_id } });
            }}
            className="w-full cursor-pointer"
            type="button"
            variant={'outline'}
            disabled={isResendCheckpointPending || isRequestCheckpointPending}
          >
            {isRequestCheckpointPending ? <LoaderIcon className="animate-spin" size={20} /> : t('ProviderWizard.tryAnotherWay')}
          </Button>
        )}
      </div>
    </WizardStepContainer>
  );
}
